import PropTypes from 'prop-types';

import {
    Swiper,
    SwiperSlide
} from 'swiper/react';

import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';


import Button from '$partials/ui/button.jsx';

import {
    IconArrowRight,
    IconArrowLeft
} from '$partials/icons';

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const CarouselContext = createContext();

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Carousel
 * @props (see CarouselPropTypes)
 * @example:
 *     <Carousel {...}>
 *         <CarouselHeadingLarge />
 *         <CarouselHeadingSmall />
 *         <CarouselHeadingBody />
 *         <CarouselViewAllButton />
 *         <CarouselNavigation />
 *         <CarouselItems />
 *     </Carousel>
 */
const Carousel = ({
    children = [],
    swiperConfig = {}
}) => {
    // State
    const [ config, setConfig ] = useState(null);
    const [ swiper, setSwiper ] = useState(null);
    const [ swiperIsEnd, setSwiperIsEnd ] = useState(null);
    const [ swiperIsStart, setSwiperIsStart ] = useState(null);

    // Methods
    const updateSwiperState = (swpr) => {
        setSwiper(swpr);
        setSwiperIsEnd(swpr.isEnd);
        setSwiperIsStart(swpr.isBeginning);
    };

    // Hooks
    useEffect(() => {
        setConfig({
            onResize: updateSwiperState,
            onSwiper: updateSwiperState,
            onTransitionEnd: updateSwiperState,
            wrapperTag: 'ul',
            ...swiperConfig
        });
    }, [ swiperConfig ]);

    // Render
    return (
        <CarouselContext.Provider value={useMemo(() => {
            return {
                config,
                swiper,
                swiperIsEnd,
                swiperIsStart
            };
        }, [
            config,
            swiper,
            swiperIsEnd,
            swiperIsStart
        ])}>
            {children}
        </CarouselContext.Provider>
    );
};


/**
 * @component CarouselHeadingBody
 * @props (see CarouselHeadingBodyPropTypes)
 */
const CarouselHeadingBody = ({ children = '' }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselHeadingBody must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <p className='type-style-4'>
            {children}
        </p>
    );
};


/**
 * @component CarouselHeadingLarge
 * @props (see CarouselHeadingLargePropTypes)
 */
const CarouselHeadingLarge = ({ children = '' }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselHeadingLarge must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <h2 className='type-style-1'>
            {children}
        </h2>
    );
};


/**
 * @component CarouselHeadingSmall
 * @props (see CarouselHeadingSmallPropTypes)
 */
const CarouselHeadingSmall = ({ children = '' }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselHeadingSmall must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <h2 className='type-style-3'>
            {children}
        </h2>
    );
};


/**
 * @component CarouselItems
 * @props (see CarouselItemsPropTypes)
 */
const CarouselItems = ({ items = [] }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselItems must be used within a CarouselContext.Provider');
    }
    // Render
    return (
        <Swiper
            {...cc.config}
            key={cc.config} /* bug: swiper does not rerender even when wrapping context values changed, terrible i know */ >
            {items?.map((itm, idx) => {
                return (
                    <SwiperSlide
                        aria-roledescription='slide'
                        className={itm?.props?.slideClassName || null}
                        key={idx}
                        role='group'
                        tag='li'>
                        {itm}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};


/**
 * @component CarouselViewAllButton
 * @props (see CarouselViewAllButtonPropTypes)
 */
const CarouselViewAllButton = ({ url = null }) => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselViewAllButton must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <div>
            <a
                className='pill-button'
                href={url}>
                <span className='type-style-5'>
                    View All
                </span>
            </a>
        </div>
    );
};


/**
 * @component CarouselNavigation
 * @props (see CarouselNavigationPropTypes)
 */
const CarouselNavigation = () => {
    // Contexts
    const cc = useContext(CarouselContext);

    // Context Check
    if (cc === undefined) {
        throw new Error('CarouselNavigation must be used within a CarouselContext.Provider');
    }

    // Render
    return (
        <div
            aria-label='slide controls'
            className='flex gap-10'
            role='group'>
            <Button
                className='round-button'
                disabled={cc.swiperIsStart}
                onClick={() => cc.swiper?.slidePrev()}
                renderIcon={<IconArrowLeft />}
                type='button'>
                <span className='sr-only'>
                    Previous
                </span>
            </Button>

            <Button
                className='round-button'
                disabled={cc.swiperIsEnd}
                onClick={() => cc.swiper?.slideNext()}
                renderIcon={<IconArrowRight />}
                type='button'>
                <span className='sr-only'>
                    Next
                </span>
            </Button>
        </div>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

const CarouselPropTypes = {
    children: PropTypes.any.isRequired,
    swiperConfig: PropTypes.object.isRequired
};

const CarouselHeadingBodyPropTypes = {
    children: PropTypes.string.isRequired
};

const CarouselHeadingLargePropTypes = {
    children: PropTypes.string.isRequired
};

const CarouselHeadingSmallPropTypes = {
    children: PropTypes.string.isRequired
};

const CarouselItemsPropTypes = {
    items: PropTypes.array.isRequired
};

const CarouselNavigationPropTypes = {};

const CarouselViewAllButtonPropTypes = {
    url: PropTypes.string.isRequired
};

Carousel.propTypes = CarouselPropTypes;
CarouselHeadingBody.propTypes = CarouselHeadingBodyPropTypes;
CarouselHeadingLarge.propTypes = CarouselHeadingLargePropTypes;
CarouselHeadingSmall.propTypes = CarouselHeadingSmallPropTypes;
CarouselItems.propTypes = CarouselItemsPropTypes;
CarouselNavigation.propTypes = CarouselNavigationPropTypes;
CarouselViewAllButton.propTypes = CarouselViewAllButtonPropTypes;

CarouselContext.displayName = 'CarouselContext';

export {
    Carousel,
    CarouselHeadingBody,
    CarouselHeadingLarge,
    CarouselHeadingSmall,
    CarouselItems,
    CarouselNavigation,
    CarouselViewAllButton
};
